import React, { useEffect, useState } from 'react';
import { CalendarClock, Clock, Download, ExternalLink, FileText, LockKeyhole, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import FeatureGate from '../components/FeatureGate';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatCard from '../components/StatCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { analyticsApi } from '../lib/api';

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatPeriod(from, to) {
  const start = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(from));
  const end = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(to));
  return `${start} - ${end}`;
}

function getDefaultRange(type) {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (type === 'monthly' ? 30 : 7));
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

function LockedGenerateButton() {
  return (
    <button
      className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-400"
      type="button"
    >
      <LockKeyhole className="h-4 w-4" />
      Generate Report
    </button>
  );
}

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState([]);
  const [tablePage, setTablePage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('weekly');
  const [requestedRange, setRequestedRange] = useState(() => getDefaultRange('weekly'));

  const loadReports = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await analyticsApi.reports({ page: 1, page_size: 100 });
      setReports(response.data?.items || []);
    } catch (error) {
      toast.error('Failed to load reports.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    setRequestedRange(getDefaultRange(reportType));
  }, [reportType]);

  const handleGenerate = async (event) => {
    event.preventDefault();
    setGenerating(true);

    try {
      await analyticsApi.generateReport(reportType);
      toast.success(`${reportType} report generated.`);
      setDialogOpen(false);
      loadReports({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  };

  const openHtml = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/html`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Unable to open report HTML.');
    }
  };

  const downloadHtml = async (reportId, type) => {
    try {
      const response = await api.get(`/reports/${reportId}/html`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${type || 'report'}-${reportId}.html`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Unable to download report.');
    }
  };

  const pageSize = 10;
  const sortedReports = [...reports].sort(
    (first, second) => new Date(second.generated_at).getTime() - new Date(first.generated_at).getTime()
  );
  const paginatedReports = sortedReports.slice((tablePage - 1) * pageSize, tablePage * pageSize);
  const lastGenerated = sortedReports[0]?.generated_at;
  const monthlyShare = sortedReports.length
    ? (sortedReports.filter((report) => report.type === 'monthly').length / sortedReports.length) * 100
    : 0;

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 text-white" data-testid="reports-page">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Reporting</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-white">Reports</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Generated reporting history with quick HTML preview and on-demand generation.
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            className="border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
            onClick={() => loadReports({ silent: true })}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <FeatureGate feature="scheduled_reports" fallback={<LockedGenerateButton />}>
            <Button className="bg-blue-500 text-white hover:bg-blue-400" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Generate Report
            </Button>
          </FeatureGate>
        </div>
      </div>

      {loading ? (
        <>
          <LoadingSkeleton type="card" />
          <LoadingSkeleton type="table" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <StatCard
              title="Total Reports"
              value={sortedReports.length}
              change={monthlyShare}
              changeLabel="monthly share"
              icon={<FileText className="h-5 w-5" />}
              color="blue"
            />
            <StatCard
              title="Last Generated"
              value={lastGenerated ? formatTimestamp(lastGenerated) : 'Never'}
              change={lastGenerated ? 100 : 0}
              changeLabel="most recent run"
              icon={<Clock className="h-5 w-5" />}
              color="green"
            />
          </div>

          <FeatureGate feature="scheduled_reports">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="flex items-center gap-2 text-white">
                  <CalendarClock className="h-5 w-5 text-blue-400" />
                  Scheduled Report Delivery
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Automate weekly and monthly executive summaries so stakeholders get fresh reporting
                  without manual exports.
                </p>
              </CardHeader>
              <CardContent className="grid gap-4 pt-6 lg:grid-cols-[minmax(0,1.2fr),220px] lg:items-center">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-700 bg-slate-900/80 p-4">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Weekly</p>
                    <p className="mt-2 text-lg font-semibold text-white">Monday, 8:00 AM</p>
                    <p className="mt-2 text-sm text-slate-400">
                      Leadership-ready summary of alerts, productivity, and risk posture.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-700 bg-slate-900/80 p-4">
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Monthly</p>
                    <p className="mt-2 text-lg font-semibold text-white">1st day, 9:00 AM</p>
                    <p className="mt-2 text-sm text-slate-400">
                      Longer retention window with trend snapshots for audits and board updates.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-blue-500/20 bg-blue-500/10 p-4">
                  <p className="text-sm font-medium text-white">Premium reporting workflow</p>
                  <p className="mt-2 text-sm text-slate-300">
                    Scheduled delivery is included on Professional and Enterprise tiers.
                  </p>
                  <Button className="mt-4 w-full bg-blue-500 text-white hover:bg-blue-400" onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4" />
                    Create Scheduled Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </FeatureGate>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="text-white">Report Library</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {sortedReports.length ? (
                <DataTable
                  columns={[
                    {
                      key: 'type',
                      label: 'Report Type',
                      render: (value) => (
                        <span className="capitalize text-slate-200">{String(value).replace('_', ' ')}</span>
                      ),
                    },
                    {
                      key: 'period',
                      label: 'Period',
                      render: (_, row) => formatPeriod(row.period_from, row.period_to),
                    },
                    {
                      key: 'generated_at',
                      label: 'Generated At',
                      render: (value) => formatTimestamp(value),
                    },
                    {
                      key: 'actions',
                      label: 'Actions',
                      render: (_, row) => (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-700"
                            onClick={(event) => {
                              event.stopPropagation();
                              openHtml(row.id);
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                            View HTML
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-700"
                            onClick={(event) => {
                              event.stopPropagation();
                              downloadHtml(row.id, row.type);
                            }}
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      ),
                    },
                  ]}
                  data={paginatedReports}
                  loading={loading}
                  emptyMessage="No reports have been generated yet."
                  pagination={{
                    page: tablePage,
                    pageSize,
                    total: sortedReports.length,
                    onPageChange: setTablePage,
                  }}
                />
              ) : (
                <EmptyState
                  icon={<FileText className="h-8 w-8" />}
                  title="No reports yet"
                  description="Generate a weekly or monthly report to populate the reporting library."
                  action={
                    <FeatureGate feature="scheduled_reports" fallback={<LockedGenerateButton />}>
                      <Button className="bg-blue-500 text-white hover:bg-blue-400" onClick={() => setDialogOpen(true)}>
                        <Plus className="h-4 w-4" />
                        Generate Report
                      </Button>
                    </FeatureGate>
                  }
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-white">Generate Report</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleGenerate}>
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Report type</label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="border-slate-700 bg-slate-900 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">From</label>
                <input
                  type="date"
                  value={requestedRange.from}
                  onChange={(event) =>
                    setRequestedRange((current) => ({ ...current, from: event.target.value }))
                  }
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-200 outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">To</label>
                <input
                  type="date"
                  value={requestedRange.to}
                  onChange={(event) =>
                    setRequestedRange((current) => ({ ...current, to: event.target.value }))
                  }
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-200 outline-none"
                />
              </div>
            </div>

            <p className="rounded-xl border border-slate-700 bg-slate-900/80 p-3 text-xs text-slate-500">
              Current backend behavior: the selected report type controls the final reporting period.
              The date range above is captured in the UI, but the server still generates its canonical
              weekly or monthly window.
            </p>

            <Button className="w-full bg-blue-500 text-white hover:bg-blue-400" disabled={generating} type="submit">
              {generating ? 'Generating...' : 'Generate'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
